import { GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import { DateTime } from 'luxon';
import * as React from 'react';
import { useCookies } from 'react-cookie';

export enum Preference {
  DataGridDensity = 'dataGridDensity',
  IncidentUnattachedCutoffDate = 'incidentUnattachedCutoffDate',
  IncidentGridColumns = 'incidentGridColumns',
  FlightEventReportGridColumns = 'flightEventReportGridColumns',
  DispatchEventReportGridColumns = 'dispatchEventReportGridColumns',
  CabinEventReportGridColumns = 'cabinEventReportGridColumns',
  FlightOperationNotificationGridColumns = 'flightOperationNotificationGridColumns',
  SafetyEventReportGridColumns = 'safetyEventReportGridColumns',
}
type TPreferences = {
  dataGridDensity: 'compact' | 'standard' | 'comfortable';
  incidentUnattachedCutoffDate: string;
  updatePreference: (key: Preference, value: any) => void;
  incidentGridColumns: GridColumnVisibilityModel;
  flightEventReportGridColumns: GridColumnVisibilityModel;
  dispatchEventReportGridColumns: GridColumnVisibilityModel;
  cabinEventReportGridColumns: GridColumnVisibilityModel;
  flightOperationNotificationGridColumns: GridColumnVisibilityModel;
  safetyEventReportGridColumns: GridColumnVisibilityModel;
};
interface IPreferencesProvider {
  children: React.ReactNode;
}

const PreferencesContext = React.createContext<TPreferences>(undefined!);

export function PreferencesProvider({ children }: IPreferencesProvider) {
  const [cookies, setCookie] = useCookies();
  const [dataGridDensity, setDataGridDensity] = React.useState<'compact' | 'standard' | 'comfortable'>('standard');
  const [incidentUnattachedCutoffDate, setIncidentUnattachedCutoffDate] = React.useState<string>(DateTime.now().minus({ days: 14 }).toISO());
  const [incidentGridColumns, setIncidentGridColumns] = React.useState<GridColumnVisibilityModel>({});
  const [flightEventReportGridColumns, setFlightEventReportGridColumns] = React.useState<GridColumnVisibilityModel>({});
  const [dispatchEventReportGridColumns, setDispatchEventReportGridColumns] = React.useState<GridColumnVisibilityModel>({});
  const [cabinEventReportGridColumns, setCabinEventReportGridColumns] = React.useState<GridColumnVisibilityModel>({});
  const [flightOperationNotificationGridColumns, setFlightOperationNotificationGridColumns] = React.useState<GridColumnVisibilityModel>({});
  const [safetyEventReportGridColumns, setSafetyEventReportGridColumns] = React.useState<GridColumnVisibilityModel>({});

  React.useEffect(() => {
    if (cookies.dataGridDensity !== dataGridDensity) {
      setDataGridDensity(cookies.dataGridDensity ?? dataGridDensity);
    }

    if (cookies.incidentUnattachedCutoffDate !== incidentUnattachedCutoffDate) {
      setIncidentUnattachedCutoffDate(cookies.incidentUnattachedCutoffDate ?? incidentUnattachedCutoffDate);
    }

    if (cookies.incidentGridColumns !== incidentGridColumns) {
      setIncidentGridColumns(cookies.incidentGridColumns ?? incidentGridColumns);
    }

    if (cookies.flightEventReportGridColumns !== flightEventReportGridColumns) {
      setFlightEventReportGridColumns(cookies.flightEventReportGridColumns ?? flightEventReportGridColumns);
    }

    if (cookies.dispatchEventReportGridColumns !== dispatchEventReportGridColumns) {
      setDispatchEventReportGridColumns(cookies.dispatchEventReportGridColumns ?? dispatchEventReportGridColumns);
    }

    if (cookies.cabinEventReportGridColumns !== cabinEventReportGridColumns) {
      setCabinEventReportGridColumns(cookies.cabinEventReportGridColumns ?? cabinEventReportGridColumns);
    }

    if (cookies.flightOperationNotificationGridColumns !== flightOperationNotificationGridColumns) {
      setFlightOperationNotificationGridColumns(cookies.flightOperationNotificationGridColumns ?? flightOperationNotificationGridColumns);
    }

    if (cookies.safetyEventReportGridColumns !== safetyEventReportGridColumns) {
      setSafetyEventReportGridColumns(cookies.safetyEventReportGridColumns ?? safetyEventReportGridColumns);
    }
  }, [cookies]);

  const handleSetPreference = (key: Preference, value: any) => {
    setCookie(key, value, { path: '/' });
  };

  const preferences = React.useMemo<TPreferences>(() => ({
    dataGridDensity,
    incidentUnattachedCutoffDate,
    updatePreference: (key: Preference, value: any) => handleSetPreference(key, value),
    incidentGridColumns,
    flightEventReportGridColumns,
    dispatchEventReportGridColumns,
    cabinEventReportGridColumns,
    flightOperationNotificationGridColumns,
    safetyEventReportGridColumns,
  }), [dataGridDensity,
    incidentUnattachedCutoffDate,
    incidentGridColumns,
    flightEventReportGridColumns,
    dispatchEventReportGridColumns,
    cabinEventReportGridColumns,
    flightOperationNotificationGridColumns,
    safetyEventReportGridColumns]);

  return (
    <PreferencesContext.Provider value={preferences}>
      {children}
    </PreferencesContext.Provider>
  );
}

export const usePreferences = () => React.useContext(PreferencesContext);
